import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { RecuperacionUsuarioService } from '@core/services/auth/recuperacion-usuario.service';
import { TokenService } from '@core/services/auth/token.service';
import { MensajeErrorState } from '@core/store/mensaje-error.store';
import { MensajesGenericosConstants } from '@shared/constants/mensajes-genericos-constants';
import { catchError, EMPTY, finalize, switchMap, filter, take, BehaviorSubject } from 'rxjs';

// Subject para manejar la cola de peticiones durante el refresh
let tokenRefreshSubject: BehaviorSubject<boolean> | null = null;

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
    const tokenService = inject(TokenService);
    const tiempoGracia = 5 * 60 * 1000; // 5 minutos

    if (req.url === AuthService.LOGIN_URL || RecuperacionUsuarioService.esUrlPublica(req.url)) {
        return next(req);
    }

    if (req.url === AuthService.REFRESH_URL) {
        const requestClone = tokenService.addTokenHeader(req);
        return next(requestClone);
    }

    // Si está refrescando, esperar a que termine en lugar de retornar EMPTY
    if (tokenService.isRefreshing && tokenRefreshSubject) {
        return tokenRefreshSubject.pipe(
            filter(refreshed => refreshed === true),
            take(1),
            switchMap(() => {
                const requestClone = tokenService.addTokenHeader(req);
                return next(requestClone);
            })
        );
    }

    const dataUser = tokenService.getDataUser();

    if (!dataUser?.accessToken) {
        inject(Router).navigateByUrl('/');
        return EMPTY;
    }

    const tiempoExpiracion = tokenService.timeExp();

    if (tiempoExpiracion > 0 && tiempoExpiracion < tiempoGracia) {
        const authService = inject(AuthService);
        const mensajeErrorState = inject(MensajeErrorState);
        const router = inject(Router);
        
        // Si no hay un refresh en curso, iniciarlo
        if (!tokenService.isRefreshing) {
            tokenService.isRefreshing = true;
            tokenRefreshSubject = new BehaviorSubject<boolean>(false);

            authService.refreshToken().pipe(
                finalize(() => {
                    tokenService.isRefreshing = false;
                }),
                catchError((error) => {
                    tokenService.isRefreshing = false;
                    if (tokenRefreshSubject) {
                        tokenRefreshSubject.error(error);
                        tokenRefreshSubject = null;
                    }
                    mensajeErrorState.actualizarMensaje(MensajesGenericosConstants.SESION_NO_VALIDA);
                    authService.onLogout();
                    router.navigateByUrl('/');
                    return EMPTY;
                })
            ).subscribe({
                next: (response) => {
                    if (response?.access_token) {
                        tokenService.updateTokens(response);
                        // Notificar a todas las peticiones en espera que el refresh terminó
                        if (tokenRefreshSubject) {
                            tokenRefreshSubject.next(true);
                            tokenRefreshSubject.complete();
                            tokenRefreshSubject = null;
                        }
                    } else {
                        if (tokenRefreshSubject) {
                            tokenRefreshSubject.error(new Error('Refresh token fallido'));
                            tokenRefreshSubject = null;
                        }
                        mensajeErrorState.actualizarMensaje(MensajesGenericosConstants.SESION_NO_VALIDA);
                        authService.onLogout();
                        router.navigateByUrl('/');
                    }
                },
                error: (error) => {
                    if (tokenRefreshSubject) {
                        tokenRefreshSubject.error(error);
                        tokenRefreshSubject = null;
                    }
                }
            });
        }

        // Esperar a que termine el refresh y luego continuar con la petición
        if (tokenRefreshSubject) {
            return tokenRefreshSubject.pipe(
                filter(refreshed => refreshed === true),
                take(1),
                switchMap(() => {
                    const requestClone = tokenService.addTokenHeader(req);
                    return next(requestClone);
                }),
                catchError(() => {
                    return EMPTY;
                })
            );
        }
    }
    
    const requestClone = tokenService.addTokenHeader(req);
    return next(requestClone);
};
